// src/app/app.routes.ts
import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { organizationGuard } from './guards/organization.guard';
import { hasOrganizationGuard } from './guards/has-organization.guard';
import { accountInactiveGuard } from './guards/account-inactive.guard';

export const routes: Routes = [
  // Public routes (no layout)
  {
    path: 'login',
    loadComponent: () =>
      import('./components/login/login.component').then(
        (m) => m.LoginComponent,
      ),
  },
  {
    path: 'account-inactive',
    loadComponent: () =>
      import('./components/account-inactive/account-inactive.component').then(
        (m) => m.AccountInactiveComponent,
      ),
    canActivate: [accountInactiveGuard],
  },
  {
    path: 'error',
    loadComponent: () =>
      import('./components/error-page/error-page.component').then(
        (m) => m.ErrorPageComponent,
      ),
  },
  {
    path: 'meeting/:roomId',
    loadComponent: () =>
      import('./components/meeting/meeting.component').then(
        (m) => m.MeetingComponent,
      ),
  },
  {
    path: 'auth-callback',
    redirectTo: '/',
    pathMatch: 'full',
  },
  {
    path: 'accept-invite/:token',
    loadComponent: () =>
      import('./components/accept-invite/accept-invite.component').then(
        (m) => m.AcceptInviteComponent,
      ),
  },
  {
    path: 'customer-upload/:token',
    loadComponent: () =>
      import('./components/customer-upload/customer-upload.component').then(
        (m) => m.CustomerUploadComponent,
      ),
  },
  // Onboarding routes (authenticated, no topbar)
  {
    path: 'onboarding',
    loadComponent: () =>
      import('./layouts/onboarding-layout/onboarding-layout').then(
        (m) => m.OnboardingLayoutComponent,
      ),
    canActivate: [authGuard],
    children: [
      {
        path: 'signup-process',
        loadComponent: () =>
          import('./components/signup-process/signup-process.component').then(
            (m) => m.SignupProcessComponent,
          ),
        canActivate: [organizationGuard],
      },
    ],
  },
  // Authenticated routes (with layout)
  {
    path: '',
    loadComponent: () =>
      import('./layouts/authenticated-layout/authenticated-layout').then(
        (m) => m.AuthenticatedLayoutComponent,
      ),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./components/home/home.component').then(
            (m) => m.HomeComponent,
          ),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'home',
        redirectTo: '',
        pathMatch: 'full',
      },
      {
        path: 'organization',
        loadComponent: () =>
          import('./components/organization/organization.component').then(
            (m) => m.OrganizationComponent,
          ),
      },
      {
        path: 'meetings',
        loadComponent: () =>
          import('./components/meetings/meetings.component').then(
            (m) => m.MeetingsComponent,
          ),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'recordings',
        loadComponent: () =>
          import('./components/recordings/recordings.component').then(
            (m) => m.RecordingsComponent,
          ),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'customers/new',
        loadComponent: () =>
          import(
            './components/customers/customer-form/customer-form.component'
          ).then((m) => m.CustomerFormComponent),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'customers/:id/edit',
        loadComponent: () =>
          import(
            './components/customers/customer-form/customer-form.component'
          ).then((m) => m.CustomerFormComponent),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'customers/:id',
        loadComponent: () =>
          import(
            './components/customers/customer-form/customer-form.component'
          ).then((m) => m.CustomerFormComponent),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'customers',
        loadComponent: () =>
          import('./components/customers/customers.component').then(
            (m) => m.CustomersComponent,
          ),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'knowledge',
        loadComponent: () =>
          import('./components/knowledge/knowledge-base').then(
            (m) => m.KnowledgeBase,
          ),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'cases/new',
        loadComponent: () =>
          import('./components/cases/case-form/case-form.component').then(
            (m) => m.CaseFormComponent,
          ),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'cases/:id/edit',
        loadComponent: () =>
          import('./components/cases/case-form/case-form.component').then(
            (m) => m.CaseFormComponent,
          ),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'cases/:id',
        loadComponent: () =>
          import('./components/cases/case-form/case-form.component').then(
            (m) => m.CaseFormComponent,
          ),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'cases',
        loadComponent: () =>
          import('./components/cases/cases.component').then(
            (m) => m.CasesComponent,
          ),
        canActivate: [hasOrganizationGuard],
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./components/settings/settings.component').then(
            (m) => m.SettingsComponent,
          ),
        canActivate: [hasOrganizationGuard],
      },
    ],
  },
  {
    path: '**',
    redirectTo: '/login',
  },
];
